import {
  AssetsProvider,
  css,
  Image,
  Link,
  mobile,
  notDesktop,
  styled,
  Text,
  useClipboard,
  useRouter,
  useTimeout,
} from '@obvio/app'
import { SvgBookmark, SvgShare } from '@obvio/svg'
import { Grid, Stack, Tab, Tabs } from '@obvio/ui'
import { motion } from 'framer-motion'

import { FormNewsletter } from './FormNewsletter'
import { normalizeContent, timeToRead, formatDate } from '@/utils/articles'
import { useArticleCategories } from '@/utils/hooks/useArticleCategories'
import { useArticlesAll } from '@/utils/hooks/useArticlesAll'

import type { ImageAsset } from '@obvio/app'
import type { ReactElement } from 'react'

const StyledTabs = styled(Tabs)<{ $count: number }>`
  margin-top: 10rem;
  @media ${notDesktop} {
    margin-top: 2.5rem;
  }
  .rc-tabs-content-holder {
    padding-top: 7rem !important;
    @media ${notDesktop} {
      padding-top: 2rem !important;
    }
  }
  .rc-tabs-nav {
    position: sticky;
    top: 6rem;
    background: white;
    z-index: 2;
    padding: 2rem;
    @media ${notDesktop} {
      top: 5rem;
    }
  }
  .rc-tabs-tab {
    /* padding-bottom: 0 !important; */
    @media ${mobile} {
      min-width: 14rem;
    }
    .rc-tabs-tab-btn {
      width: 100%;
      text-align: center;
      font-family: ${(theme) => theme.secondaryFont};
      font-weight: 400;
      > div {
        padding-bottom: ${(theme) => theme.spacing.small};
      }
    }
  }
  .rc-tabs-nav-list {
    display: grid !important;
    grid-template-columns: ${(_, { $count }) => `repeat(${$count}, 1fr)`};
    @media ${mobile} {
      width: unset !important;
    }
  }
`

const pillStyle = css`
  height: 2.25rem;
  padding: 0.625rem;
  border-radius: 50px;
  background: rgba(140, 140, 140, 0.4);
  transition: background 0.2s ease-in-out;
  display: flex;
  align-items: center;
  overflow: hidden;
  p {
    color: white;
  }

  :hover {
    background: rgba(140, 140, 140, 0.75);
    cursor: pointer;
  }
`

const ShareButton = styled.button`
  ${pillStyle}
`

const CategoryPill = styled.div`
  ${pillStyle}
`

const ImageWrap = motion(styled.div<{ $big: boolean }>`
  width: 100%;
  aspect-ratio: ${(_, { $big }) => ($big ? 1.8 : 1.4)};
  overflow: hidden;
  position: relative;
  @media ${notDesktop} {
    aspect-ratio: 1.4;
  }
`)

const PillsWrap = styled(Stack)`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 1;
  font-size: 0.8125rem;
  color: white;
  font-family: ${(theme) => theme.secondaryFont};
`

const NewsletterWrap = styled.div`
  border-top: 1px solid ${(theme) => theme.stroke};
  border-bottom: 1px solid ${(theme) => theme.stroke};
  padding: ${(theme) => theme.spacing.extraLarge} 0;
`

const MotionLink = motion(Link)
const MotionGrid = motion(styled(Grid)`
  @media ${notDesktop} {
    grid-template-columns: 1fr;
  }
`)

const ArticlesGrid = styled(Grid)`
  @media ${notDesktop} {
    grid-template-columns: 1fr;
  }
`

const CenterStack = styled(Stack)`
  align-items: center;
`

type ArticleProps = {
  id: string
  createdAt: Date
  image: ImageAsset
  title: string
  slug: string
  category: string
  description: string
  big?: boolean
  content: any
}

type PillsProps = {
  category: string
  slug: string
}

function Pills({ category, slug }: PillsProps): ReactElement {
  const { value, timeout } = useTimeout(2000)
  const copy = useClipboard()
  const { setParam } = useRouter()

  const handleCopy = () => {
    copy(`${window.location.href}/${slug}`)
    timeout()
  }

  return (
    <PillsWrap>
      <ShareButton onClick={handleCopy}>
        <motion.div
          animate={value ? 'visible' : 'hidden'}
          initial={{ width: 0, opacity: 0 }}
          variants={{
            visible: { width: '7rem', opacity: 1 },
            hidden: { width: '0rem', opacity: 0 },
          }}
          transition={{ duration: 0.15, type: 'spring' }}
        >
          <Text>Skopiowano!</Text>
        </motion.div>
        <SvgShare />
      </ShareButton>
      <CategoryPill onClick={() => setParam('category', category)}>
        {category}
      </CategoryPill>
    </PillsWrap>
  )
}

function Article({
  id,
  image,
  title,
  slug,
  category,
  description,
  content,
  createdAt,
  big = false,
}: ArticleProps): ReactElement {
  return (
    <MotionGrid
      templateColumns={big ? '2fr 1fr' : '1fr'}
      gap="big"
      initial="initial"
      whileHover="hover"
    >
      <AssetsProvider path={`/article/${id}`}>
        <ImageWrap $big={big}>
          <Pills category={category} slug={slug} />
          <motion.div
            variants={{
              initial: { scale: 1 },
              hover: { scale: 1.05 },
            }}
            transition={{ duration: 0.3 }}
            style={{ width: '100%', height: '100%' }}
          >
            <Image img={image} sizes="33vw" />
          </motion.div>
        </ImageWrap>
      </AssetsProvider>
      <MotionLink href={`/blog/${slug}`} whileHover="hover">
        <Stack kind="vertical" spacing="big">
          <Text tag="h3">{title}</Text>
          <Text>{description}</Text>

          <CenterStack>
            <SvgBookmark />
            <Text as="note">
              {timeToRead(content)} min czytania · {formatDate(createdAt)}
            </Text>
          </CenterStack>
        </Stack>
      </MotionLink>
    </MotionGrid>
  )
}

type ArticlesProps = {
  category?: string
}

function Articles({ category }: ArticlesProps): ReactElement {
  const { data = [] } = useArticlesAll(category)

  return (
    <Stack kind="vertical" spacing="10rem">
      {data && data?.length > 0 ? (
        <>
          <Article
            big
            key={data[0].id}
            id={data[0].id}
            category={data[0].category.title}
            title={data[0].title}
            slug={data[0].slug}
            image={data[0].image}
            content={data[0].content}
            description={
              data[0].seoDescription || normalizeContent(data[0].content)
            }
            createdAt={data[0].createdAt}
          />
          <ArticlesGrid
            templateRows="auto 1fr"
            templateColumns="repeat(3, 1fr)"
            gap="large"
          >
            {data?.slice(1, 7).map((article) => (
              <Article
                id={article.id}
                key={article.id}
                category={article.category.title}
                title={article.title}
                slug={article.slug}
                image={article.image}
                content={article.content}
                description={
                  article.seoDescription || normalizeContent(article.content)
                }
                createdAt={article.createdAt}
              />
            ))}
          </ArticlesGrid>
        </>
      ) : null}
      <NewsletterWrap>
        <FormNewsletter />
      </NewsletterWrap>

      <ArticlesGrid
        templateRows="auto 1fr"
        templateColumns="repeat(3, 1fr)"
        gap="large"
      >
        {data?.slice(7).map((article) => (
          <Article
            id={article.id}
            key={article.id}
            category={article.category.title}
            title={article.title}
            slug={article.slug}
            image={article.image}
            content={article.content}
            description={
              article.seoDescription || normalizeContent(article.content)
            }
            createdAt={article.createdAt}
          />
        ))}
      </ArticlesGrid>
    </Stack>
  )
}

export function ArticlesAll(): ReactElement {
  const { setParam, query } = useRouter()
  const { data: categories } = useArticleCategories()
  return (
    <>
      <Text tag="h2" as="h1">
        Artykuły
      </Text>
      <StyledTabs
        $count={(categories?.length ?? 0) + 1}
        activeKey={query?.category ?? 'all'}
        onChange={(v) => setParam('category', v)}
      >
        <Tab label="Wszystkie" key="all">
          <Articles />
        </Tab>
        {categories?.map((category) => (
          <Tab key={category.title} label={category.title}>
            <Articles category={category.title} />
          </Tab>
        ))}
      </StyledTabs>
    </>
  )
}
